import React, { createContext, useState, useEffect, useContext } from 'react';
import { 
  CognitoIdentityProviderClient, 
  InitiateAuthCommand, 
  SignUpCommand, 
  ConfirmSignUpCommand, 
  GetUserCommand,
  ResendConfirmationCodeCommand,
  ForgotPasswordCommand,
  ConfirmForgotPasswordCommand,
  RespondToAuthChallengeCommand
} from "@aws-sdk/client-cognito-identity-provider";
import CryptoJS from 'crypto-js';

// Create Auth Context
const AuthContext = createContext(null);

// Cognito configuration
const REGION = "eu-west-1";
const USER_POOL_ID = "eu-west-1_lohaqcqwd"; // Used for potential future admin operations
const CLIENT_ID = "4p77gau1vl0j6gaf2pi3taf7et";
const CLIENT_SECRET = "qnaf195jmk2oi3m4o9annc1qegee9erf11837b0p6217f4h6kpg";

// Create Cognito client
const cognitoClient = new CognitoIdentityProviderClient({ 
  region: REGION
});

// Function to calculate SECRET_HASH
const calculateSecretHash = (username) => {
  const message = username + CLIENT_ID;
  const hmac = CryptoJS.HmacSHA256(message, CLIENT_SECRET);
  return CryptoJS.enc.Base64.stringify(hmac);
};

// Development mode flag - set to false to enable real authentication
const DEV_MODE = false;

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(DEV_MODE);
  const [user, setUser] = useState(DEV_MODE ? { username: 'dev-user', email: 'dev@example.com' } : null);
  const [loading, setLoading] = useState(!DEV_MODE);
  const [error, setError] = useState(null);
  const [, setTokens] = useState(null); // tokens not used directly but kept for localStorage management

  // Check if user is already logged in
  useEffect(() => {
    const checkAuth = async () => {
      // If in development mode, automatically authenticate
      if (DEV_MODE) {
        setIsAuthenticated(true);
        setLoading(false);
        return;
      }
      
      try {
        // Check if we have tokens in localStorage
        const storedTokens = localStorage.getItem('tokens');
        if (storedTokens) {
          const parsedTokens = JSON.parse(storedTokens);
          setTokens(parsedTokens);
          
          // Get user info using the access token
          try {
            const command = new GetUserCommand({
              AccessToken: parsedTokens.AccessToken,
            });
            
            const response = await cognitoClient.send(command);
            
            // Convert UserAttributes to a more usable object
            const userData = {
              username: response.Username,
              attributes: {}
            };
            
            response.UserAttributes.forEach(attr => {
              userData.attributes[attr.Name] = attr.Value;
            });
            
            // Use email as the username if available - ensures email is used as doctor identifier for DynamoDB
            if (userData.attributes.email) {
              console.log('Using email as username for doctor identification:', userData.attributes.email);
              userData.username = userData.attributes.email;
            }
            
            setUser(userData);
            setIsAuthenticated(true);
          } catch (err) {
            // Token might be expired, clear localStorage
            console.error('Error getting user data:', err);
            localStorage.removeItem('tokens');
            setIsAuthenticated(false);
          }
        } else {
          setIsAuthenticated(false);
        }
      } catch (err) {
        console.error('Auth check error:', err);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Sign up function
  const signUp = async (username, password, email) => {
    // If in development mode, simulate successful signup
    if (DEV_MODE) {
      console.log('DEV MODE: Simulating successful signup');
      return { success: true };
    }
    
    setError(null);
    
    try {
      const command = new SignUpCommand({
        ClientId: CLIENT_ID,
        Username: username,
        Password: password,
        SecretHash: calculateSecretHash(username),
        UserAttributes: [
          {
            Name: 'email',
            Value: email
          }
        ]
      });
      
      const response = await cognitoClient.send(command);
      return response;
    } catch (err) {
      console.error('Sign up error:', err);
      setError(err.message || 'Failed to sign up');
      throw err;
    }
  };

  // Confirm sign up function
  const confirmSignUp = async (username, code) => {
    // If in development mode, simulate successful confirmation
    if (DEV_MODE) {
      console.log('DEV MODE: Simulating successful confirmation');
      return { success: true };
    }
    
    setError(null);
    
    try {
      const command = new ConfirmSignUpCommand({
        ClientId: CLIENT_ID,
        Username: username,
        ConfirmationCode: code,
        SecretHash: calculateSecretHash(username)
      });
      
      const response = await cognitoClient.send(command);
      return response;
    } catch (err) {
      console.error('Confirm sign up error:', err);
      setError(err.message || 'Failed to confirm sign up');
      throw err;
    }
  };

  // Sign in function
  const signIn = async (username, password) => {
    // If in development mode, simulate successful login
    if (DEV_MODE) {
      console.log('DEV MODE: Simulating successful login');
      setUser({ username: 'dev-user', email: 'dev@example.com' });
      setIsAuthenticated(true);
      return { 
        success: true,
        AuthenticationResult: {
          AccessToken: 'dev-access-token',
          IdToken: 'dev-id-token',
          RefreshToken: 'dev-refresh-token'
        }
      };
    }
    
    setError(null);
    
    try {
      const command = new InitiateAuthCommand({
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: CLIENT_ID,
        AuthParameters: {
          USERNAME: username,
          PASSWORD: password,
          SECRET_HASH: calculateSecretHash(username)
        }
      });
      
      const response = await cognitoClient.send(command);
      
      // Check if this is a first-time login requiring password change
      if (response.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
        console.log('First-time login detected - NEW_PASSWORD_REQUIRED challenge');
        return {
          challengeName: 'NEW_PASSWORD_REQUIRED',
          session: response.Session,
          challengeParameters: response.ChallengeParameters || {}
        };
      }
      
      // Regular successful login - store tokens and authenticate
      localStorage.setItem('tokens', JSON.stringify(response.AuthenticationResult));
      setTokens(response.AuthenticationResult);
      
      // Get user info using the access token
      try {
        const userCommand = new GetUserCommand({
          AccessToken: response.AuthenticationResult.AccessToken,
        });
        
        const userResponse = await cognitoClient.send(userCommand);
        
        // Convert UserAttributes to a more usable object
        const userData = {
          username: userResponse.Username,
          attributes: {}
        };
        
        userResponse.UserAttributes.forEach(attr => {
          userData.attributes[attr.Name] = attr.Value;
        });
        
        // Use email as the username if available - ensures email is used as doctor identifier for DynamoDB
        if (userData.attributes.email) {
          console.log('Using email as username for doctor identification:', userData.attributes.email);
          userData.username = userData.attributes.email;
        }
        
        setUser(userData);
        setIsAuthenticated(true);
      } catch (err) {
        console.error('Error getting user data after login:', err);
      }
      
      return response;
    } catch (err) {
      console.error('Sign in error:', err);
      
      // Check for specific error types
      if (err.name === 'UserNotConfirmedException') {
        setError('User is not confirmed. Please check your email for a confirmation code.');
        throw new Error('User is not confirmed. Please check your email for a confirmation code.');
      } else if (err.name === 'NotAuthorizedException') {
        setError('Incorrect username or password.');
        throw new Error('Incorrect username or password.');
      } else if (err.name === 'UserNotFoundException') {
        setError('User not found.');
        throw new Error('User not found.');
      } else {
      setError(err.message || 'Failed to sign in');
        throw err;
      }
    }
  };

  // Respond to NEW_PASSWORD_REQUIRED challenge
  const respondToNewPasswordChallenge = async (username, temporaryPassword, newPassword, session) => {
    // If in development mode, simulate successful password change
    if (DEV_MODE) {
      console.log('DEV MODE: Simulating successful password challenge response');
      setUser({ username: 'dev-user', email: 'dev@example.com' });
      setIsAuthenticated(true);
      return { 
        success: true,
        AuthenticationResult: {
          AccessToken: 'dev-access-token',
          IdToken: 'dev-id-token',
          RefreshToken: 'dev-refresh-token'
        }
      };
    }
    
    setError(null);
    
    try {
      const command = new RespondToAuthChallengeCommand({
        ClientId: CLIENT_ID,
        ChallengeName: 'NEW_PASSWORD_REQUIRED',
        Session: session,
        ChallengeResponses: {
          USERNAME: username,
          NEW_PASSWORD: newPassword,
          SECRET_HASH: calculateSecretHash(username)
        }
      });
      
      const response = await cognitoClient.send(command);
      
      // Store tokens in localStorage
      localStorage.setItem('tokens', JSON.stringify(response.AuthenticationResult));
      setTokens(response.AuthenticationResult);
      
      // Get user info using the access token
      try {
        const userCommand = new GetUserCommand({
          AccessToken: response.AuthenticationResult.AccessToken,
        });
        
        const userResponse = await cognitoClient.send(userCommand);
        
        // Convert UserAttributes to a more usable object
        const userData = {
          username: userResponse.Username,
          attributes: {}
        };
        
        userResponse.UserAttributes.forEach(attr => {
          userData.attributes[attr.Name] = attr.Value;
        });
        
        // Use email as the username if available - ensures email is used as doctor identifier for DynamoDB
        if (userData.attributes.email) {
          console.log('Using email as username for doctor identification:', userData.attributes.email);
          userData.username = userData.attributes.email;
        }
        
        setUser(userData);
        setIsAuthenticated(true);
      } catch (err) {
        console.error('Error getting user data after password change:', err);
      }
      
      return response;
    } catch (err) {
      console.error('Password challenge error:', err);
      setError(err.message || 'Failed to set new password');
      throw err;
    }
  };

  // Resend confirmation code function
  const resendConfirmationCode = async (username) => {
    // If in development mode, simulate successful resend
    if (DEV_MODE) {
      console.log('DEV MODE: Simulating successful confirmation code resend');
      return { success: true };
    }
    
    setError(null);
    
    try {
      const command = new ResendConfirmationCodeCommand({
        ClientId: CLIENT_ID,
        Username: username,
        SecretHash: calculateSecretHash(username)
      });
      
      const response = await cognitoClient.send(command);
      return response;
    } catch (err) {
      console.error('Resend confirmation code error:', err);
      setError(err.message || 'Failed to resend confirmation code');
      throw err;
    }
  };

  // Forgot password function
  const forgotPassword = async (username) => {
    // If in development mode, simulate successful forgot password request
    if (DEV_MODE) {
      console.log('DEV MODE: Simulating successful forgot password request');
      return { success: true };
    }
    
    setError(null);
    
    try {
      const command = new ForgotPasswordCommand({
        ClientId: CLIENT_ID,
        Username: username,
        SecretHash: calculateSecretHash(username)
      });
      
      const response = await cognitoClient.send(command);
      return response;
    } catch (err) {
      console.error('Forgot password error:', err);
      setError(err.message || 'Failed to send password reset code');
      throw err;
    }
  };

  // Confirm forgot password function
  const confirmForgotPassword = async (username, confirmationCode, newPassword) => {
    // If in development mode, simulate successful password reset
    if (DEV_MODE) {
      console.log('DEV MODE: Simulating successful password reset');
      return { success: true };
    }
    
    setError(null);
    
    try {
      const command = new ConfirmForgotPasswordCommand({
        ClientId: CLIENT_ID,
        Username: username,
        ConfirmationCode: confirmationCode,
        Password: newPassword,
        SecretHash: calculateSecretHash(username)
      });
      
      const response = await cognitoClient.send(command);
      return response;
    } catch (err) {
      console.error('Confirm forgot password error:', err);
      setError(err.message || 'Failed to reset password');
      throw err;
    }
  };

  // Sign out function
  const signOut = () => {
    // If in development mode, just log the action
    if (DEV_MODE) {
      console.log('DEV MODE: Simulating sign out');
      return;
    }
    
    // Clear tokens from localStorage
    localStorage.removeItem('tokens');
    setTokens(null);
    setUser(null);
    setIsAuthenticated(false);
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    error,
    signUp,
    confirmSignUp,
    signIn,
    signOut,
    resendConfirmationCode,
    forgotPassword,
    confirmForgotPassword,
    respondToNewPasswordChallenge
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;